import FormattedMessage from 'I18n/components/FormattedMessage';
import { useQuery } from 'data-fetching-client';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { H1 } from 'foundations-components/transitional/components/H1';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { GET_LIMIT_USAGE } from 'pricing-pages-api/limit-usage/queries';
import { useEffect, useMemo, useState } from 'react';
import withErrorBoundary from '../components/WithErrorBoundary';
import { useIsInActiveTrial } from '../hooks/useIsInActiveTrial';
import { mutateTrackingInstance } from '../utils/tracking';
import { filterLimits, sortLimitsByHigherUsage } from '../utils/usageLimitUtils';
import LimitCard from './LimitCard.tsx';

/*
This component should only be used to show to free users in a scenario where
we are telling them to upgrade to crm-suite-starter-bundle. The BE data is set up
for this narrow scenario. The limits shown are 'free' limits, and the nextTierLimits
represent what the limits would be increased to if crm-suite-starter-bundle is
purchased. We are working on extending the BE to support additional scenarios.
*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LimitUsageSection = ({
  isFreePortal,
  earlyRequestName,
  usageTracker,
  useCompactGrid = false
}) => {
  const isInTrial = useIsInActiveTrial();
  const {
    loading,
    data: limitUsagesData
  } = useQuery(GET_LIMIT_USAGE, {
    variables: {
      earlyRequestName
    }
  });
  const limitUsages = useMemo(() => {
    if (loading || !limitUsagesData) {
      return [];
    }
    return sortLimitsByHigherUsage(filterLimits(limitUsagesData.limitUsages));
  }, [loading, limitUsagesData]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const columnCount = useMemo(() => {
    if (useCompactGrid) {
      return screenWidth >= 900 ? 2 : 1;
    }
    return screenWidth >= 1600 ? 3 : 2;
  }, [screenWidth, useCompactGrid]);

  //Usage data is not accurate for paid or trialing portals
  if (!isFreePortal || isInTrial) return null;
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 400,
      layout: "centered"
    });
  }
  if (!limitUsages.length) return null;
  return /*#__PURE__*/_jsxs(View, {
    marginBlock: "600",
    children: [/*#__PURE__*/_jsx(H1, {
      "aria-level": 2,
      marginBlockEnd: "800",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.header"
      })
    }), /*#__PURE__*/_jsx(ExpandableText
    //height to allow 3 stacked cards
    , {
      maxHeight: 900,
      expandButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.expandCards"
      }),
      collapseButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.collapseCards"
      }),
      onExpandButtonClick: () => {
        mutateTrackingInstance(usageTracker).track('limitInteraction', {
          action: 'clicked to expand limit cards section'
        });
      },
      onCollapseButtonClick: () => mutateTrackingInstance(usageTracker).track('limitInteraction', {
        action: 'clicked to collapse limit cards section'
      }),
      children: /*#__PURE__*/_jsx(CardGrid
      //small padding so the grid dropshadow is not hidden by the expandable container
      , {
        padding: "100",
        gutterSize: 16,
        columns: columnCount,
        expandToFill: true,
        children: limitUsages.map(limit => {
          return /*#__PURE__*/_jsx(LimitCard, {
            limit: limit
          }, limit.limitId);
        })
      })
    })]
  });
};
export default withErrorBoundary(LimitUsageSection, {
  boundaryName: 'LimitUsageSection'
});